import { Crown, Zap, Star, Check, X } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { getAllowedSkus } from '../../utils/skuConfig';

export const UpgradeModal = () => {
  const { session } = useAppStore();
  const allowedSkus = getAllowedSkus();

  const plans = [
    {
      name: 'Basic',
      sku: 'sku_basic',
      price: '$9.99',
      period: '/month',
      features: [
        'Basic AI features',
        '100 requests per month',
        'Email support',
        'Standard analytics'
      ],
      limitations: [
        'Limited advanced features',
        'No priority support'
      ],
      color: 'from-accent to-brand',
      current: session?.hy_sku === 'sku_basic',
      allowed: allowedSkus.includes('sku_basic')
    },
    {
      name: 'Pro',
      sku: 'sku_pro',
      price: '$19.99',
      period: '/month',
      features: [
        'Advanced AI features',
        'Unlimited requests',
        'Priority support',
        'Advanced analytics',
        'Custom integrations',
        'Team collaboration'
      ],
      limitations: [],
      color: 'from-brand to-status-warning',
      popular: true,
      current: session?.hy_sku === 'sku_pro',
      allowed: allowedSkus.includes('sku_pro')
    },
    {
      name: 'Enterprise',
      sku: 'sku_enterprise',
      price: '$49.99',
      period: '/month',
      features: [
        'All Pro features',
        'Custom AI models',
        'Dedicated support',
        'Advanced security',
        'API access',
        'White-label options',
        'SLA guarantee'
      ],
      limitations: [],
      color: 'from-status-warning to-status-warning',
      current: session?.hy_sku === 'sku_enterprise',
      allowed: allowedSkus.includes('sku_enterprise')
    }
  ];

  const handleUpgrade = (planSku: string) => {
    alert(`Plan changes for ${planSku} are not wired in this build. Use billing or account settings when available.`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="card p-6 text-center">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <Crown className="w-8 h-8 text-status-warning" />
          <h3 className="text-2xl font-bold text-th-primary">
            Upgrade Your Plan
          </h3>
        </div>
        <p className="text-th-secondary">
          Unlock more features and get the most out of your Hyper experience.
        </p>
      </div>

      {/* Current Plan Info */}
      {session && (
        <div className="card p-4 border border-brand/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-th-secondary text-sm">Current Plan</p>
              <p className="text-th-primary font-semibold">
                {session.hy_plan_dev_name} ({session.hy_sku})
              </p>
            </div>
            <div className="text-right">
              <p className="text-th-secondary text-sm">Status</p>
              <p className="text-status-success font-medium">Active</p>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans.filter(plan => plan.allowed || allowedSkus.length === 0).map((plan, _) => (
          <div
            key={plan.sku}
            className={`card p-6 relative ${
              plan.popular ? 'border-2 border-brand' : ''
            } ${
              plan.current ? 'bg-brand/5 border-brand/50' : ''
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-brand text-surface px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                  <Star className="w-3 h-3" />
                  <span>Most Popular</span>
                </div>
              </div>
            )}

            {plan.current && (
              <div className="absolute top-4 right-4">
                <div className="bg-status-success text-white px-2 py-1 rounded text-xs font-medium">
                  Current
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-th-primary mb-2">
                {plan.name}
              </h4>
              <div className="flex items-baseline justify-center space-x-1">
                <span className="text-3xl font-bold text-th-primary">
                  {plan.price}
                </span>
                <span className="text-th-secondary">
                  {plan.period}
                </span>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6">
              {plan.features.map((feature, featureIndex) => (
                <div key={featureIndex} className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-status-success flex-shrink-0" />
                  <span className="text-th-secondary text-sm">
                    {feature}
                  </span>
                </div>
              ))}
              {plan.limitations.map((limitation, limitIndex) => (
                <div key={limitIndex} className="flex items-center space-x-2">
                  <X className="w-4 h-4 text-status-error flex-shrink-0" />
                  <span className="text-th-secondary text-sm line-through">
                    {limitation}
                  </span>
                </div>
              ))}
            </div>

            {/* Action Button */}
            <button
              onClick={() => handleUpgrade(plan.sku)}
              disabled={plan.current}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-all ${
                plan.current
                  ? 'bg-status-success/20 text-status-success cursor-not-allowed'
                  : plan.popular
                  ? 'button-primary hover:scale-105'
                  : 'button-secondary hover:scale-105'
              }`}
            >
              {plan.current ? 'Current Plan' : `Upgrade to ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Upgrade Benefits */}
      <div className="card p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-4 flex items-center space-x-2">
          <Zap className="w-5 h-5 text-status-warning" />
          <span>Why Upgrade?</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h5 className="text-th-primary font-medium">Unlock Advanced Features</h5>
            <p className="text-th-secondary text-sm">
              Access powerful AI tools and advanced analytics to boost your productivity.
            </p>
          </div>
          <div className="space-y-2">
            <h5 className="text-th-primary font-medium">Priority Support</h5>
            <p className="text-th-secondary text-sm">
              Get faster response times and dedicated assistance from our support team.
            </p>
          </div>
          <div className="space-y-2">
            <h5 className="text-th-primary font-medium">No Limits</h5>
            <p className="text-th-secondary text-sm">
              Enjoy unlimited usage and remove all restrictions on your workflow.
            </p>
          </div>
          <div className="space-y-2">
            <h5 className="text-th-primary font-medium">Early Access</h5>
            <p className="text-th-secondary text-sm">
              Be the first to try new features and improvements before general release.
            </p>
          </div>
        </div>
      </div>

      {/* Upgrade Instructions */}
      <div className="bg-elevated border border-brand rounded-xl p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-3">
          How to Upgrade
        </h4>
        <div className="space-y-2 text-th-secondary">
          <p>1. Open the Hyper mobile app</p>
          <p>2. Go to Settings → Subscription</p>
          <p>3. Select your desired plan</p>
          <p>4. Complete the payment process</p>
          <p>5. Return to this app to enjoy your new features</p>
        </div>
      </div>
    </div>
  );
};
